import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";


const FOLLOWUP_CHANNEL_LABELS = {
  email: "Email",
  phone: "Téléphone",
  whatsapp: "WhatsApp",
  other: "Autre / non précisé",
};


// ACTA_AGENDA_FOLLOWUP_EDITOR_V2
export default function CommerceAgendaFollowupDialog({
  editor,
  saving,
  contactName,
  contactUrl,
  genericAction = false,
  actionTypeLabel = "Action",
  onChange,
  onSubmit,
  onComplete,
  onDelete,
  onClose,
}) {
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    setEditing(false);
  }, [editor?.action?.nextActionNoteId]);

  if (!editor) {
    return null;
  }

  const completed =
    editor?.action?.followupStatus ===
      "completed" ||
    editor?.action?.nextActionStatus ===
      "completed";

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center acta-modal-overlay p-3 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label={genericAction ? "Action" : "Relance"}
      data-testid="agenda-followup-editor"
      onClick={() => {
        if (!saving) {
          onClose();
        }
      }}
    >
      <form
        onSubmit={onSubmit}
        onClick={(event) => event.stopPropagation()}
        className="acta-modal-surface w-full rounded-3xl bg-[var(--do-bg-solid)] p-4 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] shadow-2xl sm:max-w-xl sm:p-6"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="label-eyebrow">
              Agenda commercial
            </div>

            <h2 className="mt-1 font-heading text-xl font-black text-[var(--do-ink)]">
              {genericAction
                ? "Action"
                : "Relance"}
            </h2>

            <p className="mt-1 truncate text-sm text-[var(--do-muted)]">
              {contactName}
            </p>
          </div>

          <button
            type="button"
            disabled={saving}
            onClick={onClose}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[var(--do-border)] text-xl font-black text-[var(--do-muted)] disabled:opacity-50"
            aria-label="Fermer"
          >
            ×
          </button>
        </div>

        {editing ? (
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <label className="sm:col-span-2">
              <span className="text-xs font-bold text-[var(--do-muted)]">
                Canal de relance
              </span>

              <select
                value={editor.channel || "other"}
                onChange={(event) =>
                  onChange({
                    channel:
                      event.target.value,
                  })
                }
                className="mt-2 min-h-[48px] w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 text-base text-[var(--do-ink)]"
                data-testid="agenda-followup-channel"
              >
                {Object.entries(
                  FOLLOWUP_CHANNEL_LABELS
                ).map(
                  ([value, label]) => (
                    <option
                      key={value}
                      value={value}
                    >
                      {label}
                    </option>
                  )
                )}
              </select>
            </label>

            <label>
              <span className="text-xs font-bold text-[var(--do-muted)]">
                Date
              </span>

              <input
                type="date"
                required
                value={editor.date}
                onChange={(event) => onChange({ date: event.target.value })}
                className="mt-2 min-h-[48px] w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 text-base text-[var(--do-ink)]"
                data-testid="agenda-followup-date"
              />
            </label>

            <label>
              <span className="text-xs font-bold text-[var(--do-muted)]">
                Heure
              </span>

              <input
                type="time"
                required
                value={editor.start}
                onChange={(event) => onChange({ start: event.target.value })}
                className="mt-2 min-h-[48px] w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 text-base text-[var(--do-ink)]"
                data-testid="agenda-followup-time"
              />
            </label>

            <label className="sm:col-span-2">
              <span className="text-xs font-bold text-[var(--do-muted)]">
                Relance
              </span>

              <textarea
                required
                rows={4}
                maxLength={500}
                value={editor.content}
                onChange={(event) => onChange({ content: event.target.value })}
                className="mt-2 w-full resize-y rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 py-3 text-base text-[var(--do-ink)]"
                placeholder="Objet de la relance"
                data-testid="agenda-followup-content"
              />

              <div className="mt-1 text-right text-[11px] text-[var(--do-muted)]">
                {String(editor.content || "").length}/500
              </div>
            </label>
          </div>
        ) : (
          <div className="mt-5 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl border border-[var(--do-border)] bg-[var(--do-surface-soft)] px-4 py-3">
                <div className="text-[11px] font-bold text-[var(--do-muted)]">Date</div>
                <div className="mt-1 text-sm font-black text-[var(--do-ink)]">{editor.date}</div>
              </div>

              <div className="rounded-xl border border-[var(--do-border)] bg-[var(--do-surface-soft)] px-4 py-3">
                <div className="text-[11px] font-bold text-[var(--do-muted)]">Heure</div>
                <div className="mt-1 text-sm font-black text-[var(--do-ink)]">{editor.start}</div>
              </div>
            </div>

            <div
              className="rounded-xl border border-[var(--do-border)] bg-[var(--do-surface-soft)] px-4 py-3"
              data-testid="agenda-followup-channel-label"
            >
              <div className="text-[11px] font-bold text-[var(--do-muted)]">
                {genericAction
                  ? "Type"
                  : "Canal"}
              </div>

              <div className="mt-1 text-sm font-black text-[var(--do-ink)]">
                {genericAction
                  ? actionTypeLabel
                  : (
                    FOLLOWUP_CHANNEL_LABELS[
                      editor.channel ||
                      "other"
                    ] ||
                    FOLLOWUP_CHANNEL_LABELS.other
                  )}
              </div>
            </div>

            {completed ? (
              <div
                className="rounded-xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-4 py-3 text-sm font-black text-[var(--do-accent)]"
                data-testid="agenda-followup-completed-status"
              >
                ✓ Action faite
              </div>
            ) : null}

            <div className="rounded-xl border border-[var(--do-border)] bg-[var(--do-surface-soft)] px-4 py-4">
              <div className="text-[11px] font-bold uppercase tracking-[0.08em] text-[var(--do-muted)]">
                {completed
                  ? "Action effectuée"
                  : (
                    genericAction
                      ? "Action à effectuer"
                      : "Relance à effectuer"
                  )}
              </div>
              <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-[var(--do-ink)]" data-testid="agenda-followup-description">
                {editor.content || "Aucun détail renseigné."}
              </p>
            </div>
          </div>
        )}

        {contactUrl ? (
          <div className="mt-4">
            <Link
              to={contactUrl}
              className="text-xs font-bold text-[var(--do-accent)] hover:underline"
            >
              Ouvrir la fiche du contact
            </Link>
          </div>
        ) : null}

        <div className="mt-6 border-t border-[var(--do-border)] pt-4">
          {completed ? (
            <div className="flex justify-end">
              {!genericAction ? (
                <button
                  type="button"
                  disabled={saving}
                  onClick={onDelete}
                  className="min-h-[46px] rounded-xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-4 text-sm font-black text-[var(--do-accent)] disabled:opacity-50"
                  data-testid="agenda-followup-delete"
                >
                  Supprimer
                </button>
              ) : null}
            </div>
          ) : editing && !genericAction ? (
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                disabled={saving}
                onClick={() => setEditing(false)}
                className="min-h-[46px] rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-5 text-sm font-black text-[var(--do-ink)] disabled:opacity-50"
              >
                Annuler
              </button>

              <button
                type="submit"
                disabled={saving}
                className="min-h-[46px] rounded-xl bg-[var(--do-accent)] px-5 text-sm font-black text-[var(--do-on-accent)] disabled:cursor-not-allowed disabled:opacity-50"
                data-testid="agenda-followup-save"
              >
                {saving ? "Enregistrement…" : "Enregistrer"}
              </button>
            </div>
          ) : (
            <div className={`grid gap-2 ${
              genericAction
                ? "sm:grid-cols-1"
                : "sm:grid-cols-3"
            }`}>
              {!genericAction ? (
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => setEditing(true)}
                  className="min-h-[46px] rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 text-sm font-black text-[var(--do-ink)] disabled:opacity-50"
                  data-testid="agenda-followup-edit"
                >
                  Modifier
                </button>
              ) : null}

              <button
                type="button"
                disabled={saving}
                onClick={onComplete}
                className="min-h-[46px] rounded-xl bg-[var(--do-accent)] px-4 text-sm font-black text-[var(--do-on-accent)] disabled:cursor-not-allowed disabled:opacity-50"
                data-testid="agenda-followup-complete"
              >
                {saving ? "Traitement…" : "Action faite"}
              </button>

              {!genericAction ? (
                <button
                  type="button"
                  disabled={saving}
                  onClick={onDelete}
                  className="min-h-[46px] rounded-xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-4 text-sm font-black text-[var(--do-accent)] disabled:opacity-50"
                  data-testid="agenda-followup-delete"
                >
                  Supprimer
                </button>
              ) : null}
            </div>
          )}
        </div>
      </form>
    </div>
  );
}
